import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import difference from 'lodash/difference';

import { simpleSettingSet } from 'Redux/thunks/simple-settings.thunks';
import { buildClassName } from 'Internals/react-utils';
import { HeaderButton } from '../header-button/HeaderButton.view';
import {
  AnnouncementBanner,
  AnnouncementBannerPositioner,
  AnnouncementBannerToggler,
} from './AnnouncementBanner.view';

import { TestID } from 'src/testID';

const AUTO_CLOSE_TIMEOUT = 10000;

function getAnnouncementsFromProps(props) {
  let announcements = [];

  if (props.datahubAnnouncements) {
    announcements = announcements.concat(props.datahubAnnouncements);
  }

  if (props.announcements) {
    announcements = announcements.concat(props.announcements);
  }

  return announcements;
}

/**
 * Super hacky way to detect special portal fallback message
 * @param {string} announcement
 */
function isSpecialFallbackMessage(announcement) {
  return /The Sesam portal is running in a special read-only fallback mode.*/.test(announcement);
}
class AnnouncementBannerContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isExpended: this.props.shouldBeInitiallyOpen,
    };

    this.handleToggle = (ev) => {
      ev.preventDefault();
      const newIsOpen = !this.state.isExpended;

      if (newIsOpen) {
        // the user opened the announcements, so we should never auto-close them.
        this.props.setPreventAutoClose(true);
      } else {
        // the user explicitly closed the announcements, so we can auto-close the list later.
        this.props.setPreventAutoClose(false);
      }
      this.props.setDisplayAsInitiallyClosed(!newIsOpen);

      this.setState({ isExpended: newIsOpen });
    };
  }

  componentDidMount() {
    this.scheduleAutoClose();
  }

  componentDidUpdate(prevProps) {
    // If the announcements change, we want to open the announcements-list
    const newAnnouncements = getAnnouncementsFromProps(this.props);
    const oldAnnouncements = getAnnouncementsFromProps(prevProps);
    const addedAnnouncements = difference(newAnnouncements, oldAnnouncements);
    if (addedAnnouncements.length > 0) {
      // alt least one new announcement has been added, so display the list of announcements.
      this.setState({ isExpended: true });
      this.props.setDisplayAsInitiallyClosed(false);
      this.scheduleAutoClose();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.autoCloseTimeoutId);
  }

  scheduleAutoClose() {
    if (this.autoCloseTimeoutId) {
      return;
    }

    this.autoCloseTimeoutId = setTimeout(() => {
      if (this.props.shouldAutoClose) {
        this.setState({ isExpended: false });
        this.props.setDisplayAsInitiallyClosed(true);
      }
    }, AUTO_CLOSE_TIMEOUT);
  }

  render() {
    let announcements = getAnnouncementsFromProps(this.props);

    announcements = announcements.filter((a) => {
      if (isSpecialFallbackMessage(a)) return false;
      else return true;
    });

    if (announcements.length === 0) {
      return null;
    }

    const classNames = buildClassName(
      'announcement-banner',
      this.state.isExpended ? 'announcement-banner--open' : null
    );

    return (
      <>
        <HeaderButton darkModeActive={this.props.darkModeActive}>
          <AnnouncementBannerToggler
            isExpended={this.state.isExpended}
            handleToggle={this.handleToggle}
          />
        </HeaderButton>
        <AnnouncementBannerPositioner isExpended={this.state.isExpended}>
          <AnnouncementBanner
            announcements={announcements}
            setPreventAutoClose={this.props.setPreventAutoClose}
            handleToggle={this.handleToggle}
            testId={TestID.AnnouncementBanner}
          />
        </AnnouncementBannerPositioner>
      </>
    );
  }
}

AnnouncementBannerContainer.propTypes = {
  announcements: PropTypes.arrayOf(PropTypes.string),
  datahubAnnouncements: PropTypes.arrayOf(PropTypes.string),
  isExpended: PropTypes.bool,
  shouldAutoClose: PropTypes.bool,
  setPreventAutoClose: PropTypes.func,
  setDisplayAsInitiallyClosed: PropTypes.func,
  route: PropTypes.string,
  subId: PropTypes.string,
  shouldBeInitiallyOpen: PropTypes.bool,
  darkModeActive: PropTypes.bool,
};

function mapStateToProps(state) {
  const datahubInfo = state.subscription ? state.subscription.info : null;
  const datahubAnnouncements = [];
  if (datahubInfo && datahubInfo.status) {
    const healthStatus = datahubInfo.status.health_status;
    if (healthStatus && healthStatus.status !== 'ok') {
      datahubAnnouncements.push(`The datahub is not healthy: ${healthStatus.message}`);
    }
  }

  return {
    announcements: state.user.announcements,
    route: state.routing.locationBeforeTransitions.pathname,
    datahubAnnouncements: datahubAnnouncements,
    subId: state.subscription.id,
    shouldAutoClose: !state.simpleSettings.announcementsPreventAutoClose,
    shouldBeInitiallyOpen: !state.simpleSettings.announcementsDisplayAsInitiallyClosed,
    darkModeActive: state.theme.dark,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setPreventAutoClose: (enabled) =>
      dispatch(
        simpleSettingSet({
          key: 'announcementsPreventAutoClose',
          value: enabled,
        })
      ),
    setDisplayAsInitiallyClosed: (enabled) =>
      dispatch(
        simpleSettingSet({
          key: 'announcementsDisplayAsInitiallyClosed',
          value: enabled,
        })
      ),
  };
}

const Connected = connect(mapStateToProps, mapDispatchToProps)(AnnouncementBannerContainer);

export { Connected as AnnouncementBannerContainer };
